/**
 * AppHeader — top navigation bar.
 *
 * Contains: logo, run-ID input, tenant selector, live status indicator,
 * global search pill, and user avatar.
 */

import { useState, type KeyboardEvent } from 'react'
import { Search, BarChart2, Zap, LogOut } from 'lucide-react'

import { NotificationBell } from './NotificationBell'

// ── Types ─────────────────────────────────────────────────────────────────────

interface Tenant {
  id: string
  label: string
}

export interface AppHeaderProps {
  runId: string
  onRunIdChange: (id: string) => void
  tenantId: string
  onTenantChange: (id: string) => void
  tenants?: Tenant[]
  /** Live API status — shown as a coloured dot. */
  apiStatus?: 'online' | 'degraded' | 'offline' | 'unknown'
  /** Optional callback to open the global Cmd+K search palette. */
  onSearchOpen?: () => void
  /** Opens a run in Mission Control (used by the approval notification bell). */
  onReviewRun?: (runId: string) => void
  /** Switches the whole app back to the Closr Dashboard shell. */
  onSwitchToDashboard?: () => void
  /** Clears the stored token and returns to the login page. Omitted in stub mode. */
  onLogout?: () => void
  /** Show the tenant selector. Hidden in workos mode (tenant comes from the token). */
  showTenantSwitcher?: boolean
}

// ── Constants ─────────────────────────────────────────────────────────────────

const DEFAULT_TENANTS: Tenant[] = [
  { id: 'dev-tenant', label: 'dev-tenant (local)' },
  { id: 't-acme',     label: 'ACME Corp'           },
  { id: 't-globex',   label: 'Globex Ltd'          },
]

const STATUS_DOT: Record<string, string> = {
  online:   '#22c55e',
  degraded: '#f59e0b',
  offline:  '#ef4444',
  unknown:  '#4a6280',
}

// ── Sub-components ────────────────────────────────────────────────────────────

function LiveStatusDot({ status = 'unknown' }: { status?: string }) {
  const colour = STATUS_DOT[status] ?? STATUS_DOT.unknown
  const label  = `API ${status}`
  return (
    <span
      className="flex items-center gap-1.5 flex-shrink-0 cursor-default"
      aria-label={label}
      title={label}
    >
      <span
        className={`w-[7px] h-[7px] rounded-full inline-block flex-shrink-0${status === 'online' ? ' status-ping-pulse' : ''}`}
        style={{ background: colour }}
        aria-hidden="true"
      />
      <span className="text-[11px] font-medium text-[#4a6280] capitalize">{status}</span>
    </span>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export function AppHeader({
  runId,
  onRunIdChange,
  tenantId,
  onTenantChange,
  tenants = DEFAULT_TENANTS,
  apiStatus = 'unknown',
  onSearchOpen,
  onReviewRun,
  onSwitchToDashboard,
  onLogout,
  showTenantSwitcher = true,
}: AppHeaderProps) {
  const [draft, setDraft] = useState(runId)

  // Sync draft when runId changes externally
  if (draft !== runId && document.activeElement?.getAttribute('aria-label') !== 'Run ID') {
    setDraft(runId)
  }

  function commitDraft() {
    const trimmed = draft.trim()
    if (trimmed !== runId) onRunIdChange(trimmed)
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') commitDraft()
    if (e.key === 'Escape') setDraft(runId)
  }

  const knownTenants = tenants
  const showExtra    = !knownTenants.some((t) => t.id === tenantId)

  return (
    <header
      className="[grid-area:header] flex items-center gap-3.5 px-5 h-[54px] bg-[rgba(11,18,32,0.72)] backdrop-blur-md border-b border-[rgba(148,163,184,0.10)] z-[100] flex-shrink-0"
      role="banner"
    >
      {/* ── Logo ── */}
      <div className="flex items-center gap-2.5 flex-shrink-0 no-underline" aria-label="Mission Control">
        <img
          src="/assets/brand/logo.svg"
          alt=""
          className="h-6 w-6 flex-shrink-0"
          aria-hidden="true"
        />
        <span className="text-sm font-bold text-[#e2ecf9] tracking-[-0.01em] whitespace-nowrap">
          Mission Control
        </span>
      </div>

      {/* ── Divider ── */}
      <div className="w-px h-[22px] bg-[rgba(148,163,184,0.10)] flex-shrink-0" role="separator" />

      {/* ── Run ID ── */}
      <div
        className="flex-1 max-w-[380px] flex items-center gap-2 bg-[#101a2b] border border-[rgba(148,163,184,0.10)] rounded-[10px] px-3 h-[34px] transition-[border-color,box-shadow] duration-150 focus-within:border-[#06b6d4] focus-within:shadow-[0_0_8px_rgba(6,182,212,0.30)]"
        title="Enter a Run ID to inspect"
      >
        <span className="text-[#4a6280] text-[11px] font-mono flex-shrink-0" aria-hidden="true">run:</span>
        <input
          type="text"
          placeholder="paste run ID…"
          value={draft}
          aria-label="Run ID"
          spellCheck={false}
          autoComplete="off"
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitDraft}
          onKeyDown={handleKeyDown}
          className="flex-1 bg-transparent border-none outline-none font-mono text-xs text-[#e2ecf9] min-w-0 placeholder-[#4a6280]"
        />
      </div>

      <div className="flex-1" />

      {/* ── Mode toggle (mirrors the pill in DashboardLayout) ── */}
      {onSwitchToDashboard && (
        <div className="flex items-center bg-[#101a2b] rounded-lg p-0.5 flex-shrink-0 border border-[rgba(148,163,184,0.10)]">
          <button
            onClick={onSwitchToDashboard}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[#4a6280] text-[11px] font-medium hover:text-[#e2ecf9] transition-colors"
            title="Switch to Dashboard"
          >
            <BarChart2 size={11} /> Dashboard
          </button>
          <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[rgba(6,182,212,0.14)] text-[#06b6d4] text-[11px] font-semibold border border-[rgba(6,182,212,0.25)]">
            <Zap size={11} /> Mission Control
          </span>
        </div>
      )}

      {/* ── Search trigger (opens Cmd+K palette) ── */}
      {onSearchOpen && (
        <button
          type="button"
          onClick={onSearchOpen}
          className="inline-flex items-center gap-2 rounded-md border border-[rgba(93,131,185,0.20)] px-2.5 py-1 text-xs text-[#4a6280] hover:text-[#e2ecf9] hover:border-[rgba(93,131,185,0.4)] transition-colors"
          aria-label="Open search (Cmd+K)"
          title="Open search (Cmd+K)"
        >
          <Search size={14} aria-hidden="true" />
          <span>Search</span>
          <kbd className="font-mono text-[10px] opacity-70">⌘K</kbd>
        </button>
      )}

      {/* ── Live status ── */}
      <LiveStatusDot status={apiStatus} />

      {showTenantSwitcher && (
        <>
          {/* ── Divider ── */}
          <div className="w-px h-[22px] bg-[rgba(148,163,184,0.10)] flex-shrink-0" role="separator" />

          {/* ── Tenant picker ── */}
          <div className="flex items-center gap-[7px] bg-[#101a2b] border border-[rgba(148,163,184,0.10)] rounded-[10px] px-[10px] h-[34px] flex-shrink-0">
            <span className="text-[10px] font-bold tracking-[0.1em] uppercase text-[#4a6280]">Tenant</span>
            <select
              className="bg-transparent border-none outline-none text-[#e2ecf9] font-sans text-xs cursor-pointer p-0"
              value={tenantId}
              aria-label="Active tenant"
              onChange={(e) => onTenantChange(e.target.value)}
            >
              {knownTenants.map((t) => (
                <option key={t.id} value={t.id} style={{ background: '#101a2b', color: '#e2ecf9' }}>
                  {t.label}
                </option>
              ))}
              {showExtra && (
                <option value={tenantId} style={{ background: '#101a2b', color: '#e2ecf9' }}>
                  {tenantId}
                </option>
              )}
            </select>
          </div>
        </>
      )}

      {/* ── Approval notifications ── */}
      {onReviewRun && <NotificationBell tenantId={tenantId} onReviewRun={onReviewRun} />}

      {/* ── User avatar ── */}
      <button
        className="w-[30px] h-[30px] rounded-full border border-[rgba(148,163,184,0.18)] bg-[rgba(6,182,212,0.14)] text-[#06b6d4] text-xs font-bold flex items-center justify-center cursor-pointer flex-shrink-0 transition-[box-shadow,border-color] duration-150 hover:shadow-[0_0_8px_rgba(6,182,212,0.30)] hover:border-[rgba(6,182,212,0.4)]"
        aria-label="User menu"
        title="User menu"
      >
        <span aria-hidden="true">U</span>
      </button>

      {/* ── Logout (jwt mode only) ── */}
      {onLogout && (
        <button
          type="button"
          onClick={onLogout}
          className="flex items-center justify-center w-[30px] h-[30px] rounded-full border border-[rgba(148,163,184,0.18)] text-[#4a6280] cursor-pointer flex-shrink-0 hover:text-[#ef4444] hover:border-[rgba(239,68,68,0.4)] transition-colors"
          aria-label="Log out"
          title="Log out"
        >
          <LogOut size={14} aria-hidden="true" />
        </button>
      )}
    </header>
  )
}
